import { REGISTERS_API_URL } from '../constants';
import { type AppDatabase } from '../db';
import type { PricingGroup } from '../models';
import { MAX_FETCH_PROGRESS, retrieveFullPaginatedData } from '../pagination';
import { toBooleanValueFields } from '../transform';
import { BOOLEAN_VALUE, type MappedPricingGroupResponse } from '../types';

export class PricingGroupService {
    constructor(private readonly db: AppDatabase) {
        this.db = db;
    }

    async fetchAndBulkUpsert(
        lastSyncTime?: string,
        progressCallback?: (progress: number) => void,
    ) {
        const params = {
            timestamp: lastSyncTime ?? null,
            isActive: lastSyncTime ? null : true,
        };

        await retrieveFullPaginatedData<MappedPricingGroupResponse>(
            REGISTERS_API_URL.PRODUCT.PRICING_GROUPS,
            params,
            async (data, progress) => {
                progressCallback?.(progress);
                await this.bulkUpsert(data);
            },
        );

        progressCallback?.(MAX_FETCH_PROGRESS);
    }

    async bulkUpsert(groups: MappedPricingGroupResponse[]) {
        const output = groups.map((group) => {
            return toBooleanValueFields(group, ['deleted', 'enabled']);
        });

        await this.db.pricingGroups.bulkPut(
            output as unknown as PricingGroup[],
        );
    }

    async getById(id: string) {
        return this.db.pricingGroups
            .where('[id+deleted]')
            .equals([id, BOOLEAN_VALUE.FALSE])
            .filter((group) => group.enabled === BOOLEAN_VALUE.TRUE)
            .first();
    }

    async getPricingGroupsByIds(groupIds: string[]) {
        return this.db.pricingGroups
            .where('[id+deleted]')
            .anyOf(groupIds.map((id) => [id, BOOLEAN_VALUE.FALSE]))
            .filter((group) => group.enabled === BOOLEAN_VALUE.TRUE)
            .toArray();
    }

    async getPricingGroups() {
        return this.db.pricingGroups
            .where('deleted')
            .equals(BOOLEAN_VALUE.FALSE)
            .filter((group) => group.enabled === BOOLEAN_VALUE.TRUE)
            .toArray();
    }
}
